import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTheme } from '../../contexts/ThemeContext';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
    oneDark, // for Dark Mode
    vs,
} from 'react-syntax-highlighter/dist/esm/styles/prism'; //
import 'katex/dist/katex.min.css';
import rehypeRaw from 'rehype-raw';
import { Clipboard } from 'lucide-react';
import { toast } from 'sonner'; //

// Skeleton loader component
const SkeletonLoader = ({ type = 'image' }) => {
    if (type === 'image') {
        return <div className="w-full h-64 bg-neutral-200 rounded-lg animate-pulse" />;
    }
    if (type === 'cell') {
        return (
            <div className="border border-neutral-200 rounded-lg overflow-hidden animate-pulse">
                <div className="bg-neutral-100 h-10" />
                <div className="bg-neutral-50 h-32" />
            </div>
        );
    }
    return null;
};

// Image component with lazy loading
const ImageWithLazyLoad = props => {
    const ref = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsLoaded(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { rootMargin: '50px' }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className="max-w-full h-auto rounded-lg my-6">
            {isLoaded ? <img className="max-w-full h-auto rounded-lg" {...props} /> : <SkeletonLoader type="image" />}
        </div>
    );
};

const handleCopy = async code => {
    try {
        await navigator.clipboard.writeText(code); //
        toast.success('Copied to clipboard!'); //
    } catch (err) {
        console.error(err);
        toast.error('Failed to copy!'); //
    }
};

const MarkdownComponents = (setToast, theme) => ({
    code({ inline, className, children, ...props }) {
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : 'text';
        const code = String(children).replace(/\n$/, '');

        if (inline || !className) {
            return (
                <code className="bg-neutral-200  px-1.5 py-0.5 rounded text-[0.85em] text-rose-600 dark:text-rose-400" {...props}>
                    {children}
                </code>
            );
        }

        return (
            <div className="my-4 rounded-lg overflow-hidden">
                <div className="bg-neutral-200 text-neutral-700 flex justify-between items-center px-4 py-2">
                    <span className="text-md font-semibold">{language}</span>
                    <button
                        onClick={() => handleCopy(code, setToast)}
                        className="flex items-center gap-1.5 bg-neutral-300 text-neutral-700 px-2 py-1 rounded-md hover:bg-neutral-400 transition-colors"
                        title="Copy code"
                    >
                        <Clipboard size={14} />
                    </button>
                </div>
                <SyntaxHighlighter
                    style={theme === 'dark' ? oneDark : vs}
                    language={language}
                    PreTag="div"
                    customStyle={{
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                        padding: '0.4rem',
                        margin: '-0.1rem',
                        fontFamily: '"Source Code Pro", monospace',
                        background: theme === 'dark' ? '#282c34' : '#f5f5f5',
                    }}
                    {...props}
                >
                    {code}
                </SyntaxHighlighter>
            </div>
        );
    },

    h1({ children, ...props }) {
        return (
            <h1 className="text-h1 mt-12 mb-8 text-neutral-950" {...props}>
                {children}
            </h1>
        );
    },
    h2({ children, ...props }) {
        return (
            <h2 className="text-h2 mt-10 mb-6 text-neutral-950" {...props}>
                {children}
            </h2>
        );
    },
    h3({ children, ...props }) {
        return (
            <h3 className="text-h3 mt-8 mb-4 text-neutral-950" {...props}>
                {children}
            </h3>
        );
    },
    h4({ children, ...props }) {
        return (
            <h4 className="text-h4 mt-6 mb-3 text-neutral-950" {...props}>
                {children}
            </h4>
        );
    },
    h5({ children, ...props }) {
        return (
            <h5 className="text-h5 mt-4 mb-2 text-neutral-950" {...props}>
                {children}
            </h5>
        );
    },
    h6({ children, ...props }) {
        return (
            <h6 className="text-h6 mt-3 mb-2 text-neutral-700" {...props}>
                {children}
            </h6>
        );
    },

    p({ children, ...props }) {
        // Use div instead of p to avoid nesting issues with raw HTML content
        return (
            <div className="mb-4 leading-relaxed text-neutral-700" {...props}>
                {children}
            </div>
        );
    },

    // Handle raw HTML paragraphs from rehypeRaw to prevent nesting issues
    paragraph({ children, ...props }) {
        return (
            <div className="mb-4 leading-relaxed text-neutral-700" {...props}>
                {children}
            </div>
        );
    },

    a({ children, ...props }) {
        return (
            <a
                className="text-primary hover:text-accent underline decoration-2 underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
            >
                {children}
            </a>
        );
    },

    img({ ...props }) {
        return <ImageWithLazyLoad {...props} />;
    },

    ul({ children, ...props }) {
        return (
            <ul className="list-disc pl-6 my-4 flex flex-col gap-2 text-neutral-950" {...props}>
                {children}
            </ul>
        );
    },
    ol({ children, ...props }) {
        return (
            <ol className="list-decimal pl-6 my-4 flex flex-col gap-2 text-neutral-950" {...props}>
                {children}
            </ol>
        );
    },
    li({ children, ...props }) {
        return (
            <li className="mb-2 text-neutral-700" {...props}>
                {children}
            </li>
        );
    },

    blockquote({ children, ...props }) {
        return (
            <blockquote className="border-l-4 border-accent pl-6 py-2 my-6 italic text-neutral-700 rounded-r-lg" {...props}>
                {children}
            </blockquote>
        );
    },

    table({ children, ...props }) {
        return (
            <div className="table-container my-6 overflow-x-auto rounded-lg border border-neutral-200">
                <table className="min-w-full" {...props}>
                    {children}
                </table>
            </div>
        );
    },
    thead({ children, ...props }) {
        return (
            <thead className="bg-neutral-50" {...props}>
                {children}
            </thead>
        );
    },
    tbody({ children, ...props }) {
        return (
            <tbody className="bg-neutral-50 divide-y divide-neutral-300" {...props}>
                {children}
            </tbody>
        );
    },
    tr({ children, ...props }) {
        return (
            <tr className="hover:bg-neutral-50" {...props}>
                {children}
            </tr>
        );
    },
    th({ children, ...props }) {
        return (
            <th
                className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider border-b border-neutral-300"
                {...props}
            >
                {children}
            </th>
        );
    },
    td({ children, ...props }) {
        const renderCellContent = childNodes => {
            const nodes = Array.isArray(childNodes) ? childNodes : [childNodes];

            return nodes.flatMap((node, idx) => {
                if (React.isValidElement(node)) {
                    if (typeof node.type === 'string' && (node.type === 'ul' || node.type === 'ol')) {
                        return React.cloneElement(node, {
                            className: `${node.props.className || ''} list-inside pl-4 my-2`.trim(),
                        });
                    }

                    if (typeof node.type === 'string' && node.type === 'p') {
                        return React.cloneElement(node, {
                            className: `${node.props.className || ''} mb-2 leading-relaxed`.trim(),
                        });
                    }

                    return node;
                }

                if (typeof node === 'string') {
                    return node
                        .split(/\n\s*\n/)
                        .filter(p => p !== '')
                        .map((para, pIdx) => {
                            const parts = para.split(/\n/);
                            return (
                                <p key={`cell-${idx}-${pIdx}`} className="mb-2 leading-relaxed">
                                    {parts.map((line, lIdx) => (lIdx > 0 ? [<br key={`br-${lIdx}`} />, line] : line))}
                                </p>
                            );
                        });
                }

                return node;
            });
        };

        return (
            <td className="px-6 py-4 whitespace-normal text-sm text-neutral-900 border-b border-neutral-100" {...props}>
                <div className="cell-content">{renderCellContent(children)}</div>
            </td>
        );
    },

    hr({ ...props }) {
        return <hr className="border-t-2 border-neutral-300 my-8" {...props} />;
    },

    strong({ children, ...props }) {
        return (
            <strong className="font-bold text-neutral-900" {...props}>
                {children}
            </strong>
        );
    },
    em({ children, ...props }) {
        return (
            <em className="italic text-neutral-700" {...props}>
                {children}
            </em>
        );
    },
    del({ children, ...props }) {
        return (
            <del className="line-through text-neutral-700" {...props}>
                {children}
            </del>
        );
    },
});

// Jupyter Notebook Cell Component
const JupyterCell = ({ cell, index, setToast, theme }) => {
    const ref = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const components = MarkdownComponents(setToast, theme);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        // Keep observing to handle dynamic content
                    }
                });
            },
            { rootMargin: '100px' }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => observer.disconnect();
    }, []);

    if (!isVisible) {
        return (
            <div ref={ref}>
                <SkeletonLoader type="cell" />
            </div>
        );
    }

    if (cell.cell_type === 'markdown') {
        const source = Array.isArray(cell.source) ? cell.source.join('') : cell.source;
        return (
            <div ref={ref} className="jupyter-cell markdown-cell mb-6">
                <ReactMarkdown
                    components={components}
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeKatex, rehypeRaw]}
                >
                    {source}
                </ReactMarkdown>
            </div>
        );
    }

    if (cell.cell_type === 'code') {
        const source = Array.isArray(cell.source) ? cell.source.join('') : cell.source;
        const outputs = cell.outputs || [];

        return (
            <div ref={ref} className="jupyter-cell code-cell mb-6 border border-neutral-200 rounded-lg overflow-hidden">
                {/* Code Input */}
                <div className="code-input">
                    <div className="flex items-center bg-neutral-100 px-4 py-2 border-b border-neutral-200">
                        <span className="text-xs font-mono text-neutral-700">In [{index + 1}]:</span>
                    </div>
                    <div className="rounded-b-lg overflow-hidden">
                        <div className="bg-neutral-200 text-neutral-700 flex justify-between items-center px-4 py-2">
                            <span className="text-sm font-semibold">python</span>
                            <button
                                onClick={() => handleCopy(source, setToast)}
                                className="flex items-center gap-1.5 bg-neutral-300 text-neutral-700 px-2 py-1 rounded-md hover:bg-neutral-400 transition-colors"
                                title="Copy code"
                            >
                                <Clipboard size={14} />
                            </button>
                        </div>

                        <SyntaxHighlighter
                            style={theme === 'dark' ? oneDark : vs}
                            language="python"
                            PreTag="div"
                            customStyle={{
                                margin: 0,
                                lineHeight: '1.5',
                                padding: '1rem',
                                background: theme === 'dark' ? '#282c34' : '#f5f5f5',
                            }}
                        >
                            {source}
                        </SyntaxHighlighter>
                    </div>
                </div>

                {/* Code Output */}
                {outputs.length > 0 && (
                    <div className="code-output bg-neutral-50">
                        <div className="flex items-center bg-neutral-50 px-4 py-2 border-t border-b border-neutral-300">
                            <span className="text-xs font-mono text-neutral-700">Out [{index + 1}]:</span>
                        </div>
                        {outputs.map((output, outputIdx) => (
                            <div key={outputIdx} className="output-item p-4">
                                {output.output_type === 'stream' && (
                                    <pre className="text-sm font-mono text-neutral-700 whitespace-pre-wrap">
                                        {Array.isArray(output.text) ? output.text.join('') : output.text}
                                    </pre>
                                )}
                                {output.output_type === 'execute_result' && output.data && (
                                    <>
                                        {output.data['text/plain'] && (
                                            <pre className="text-sm font-mono text-neutral-700 whitespace-pre-wrap">
                                                {Array.isArray(output.data['text/plain'])
                                                    ? output.data['text/plain'].join('')
                                                    : output.data['text/plain']}
                                            </pre>
                                        )}
                                        {output.data['text/html'] && (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: Array.isArray(output.data['text/html'])
                                                        ? output.data['text/html'].join('')
                                                        : output.data['text/html'],
                                                }}
                                            />
                                        )}
                                        {output.data['image/png'] && (
                                            <img
                                                src={`data:image/png;base64,${output.data['image/png']}`}
                                                alt="Output"
                                                className="max-w-full h-auto"
                                            />
                                        )}
                                    </>
                                )}
                                {output.output_type === 'display_data' && output.data && (
                                    <>
                                        {output.data['text/plain'] && (
                                            <pre className="text-sm font-mono text-neutral-700 whitespace-pre-wrap">
                                                {Array.isArray(output.data['text/plain'])
                                                    ? output.data['text/plain'].join('')
                                                    : output.data['text/plain']}
                                            </pre>
                                        )}
                                        {output.data['image/png'] && (
                                            <img
                                                src={`data:image/png;base64,${output.data['image/png']}`}
                                                alt="Output"
                                                className="max-w-full h-auto"
                                            />
                                        )}
                                    </>
                                )}
                                {output.output_type === 'error' && (
                                    <pre className="text-sm font-mono text-primary whitespace-pre-wrap">
                                        {output.traceback ? output.traceback.join('\n') : `${output.ename}: ${output.evalue}`}
                                    </pre>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return null;
};

const MarkdownRenderer = ({ content = '', small = false, isJupyter = false }) => {
    const [setToast] = React.useState(null);
    const { theme } = useTheme();

    // Resolve the actual theme to use for components. If the app theme is
    // `system`, derive dark/light from the OS preference so code blocks
    // (which use inline styles) follow the effective appearance.
    const effectiveTheme = (() => {
        if (typeof window === 'undefined') return theme;
        if (theme === 'system') {
            return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return theme;
    })();
    const detectJupyter = contentStr => {
        try {
            const parsed = JSON.parse(contentStr);
            return parsed.cells && Array.isArray(parsed.cells) && parsed.nbformat;
        } catch {
            return false;
        }
    };

    const isNotebook = isJupyter || detectJupyter(content);

    if (isNotebook) {
        let notebook;
        try {
            notebook = typeof content === 'string' ? JSON.parse(content) : content;
        } catch (err) {
            return <div className="text-primary p-4">Error parsing Jupyter notebook: {err.message}</div>;
        }

        // Sanitize notebook markdown cells: remove YAML frontmatter (--- ... ---)
        let sanitizedNotebook = notebook;
        try {
            sanitizedNotebook = {
                ...notebook,
                cells: (notebook.cells || []).map(cell => {
                    if (cell && cell.cell_type === 'markdown') {
                        const raw = Array.isArray(cell.source) ? cell.source.join('') : cell.source || '';
                        const cleaned = raw.replace(/^\s*---[\s\S]*?---\s*/m, '');
                        // Preserve array/string shape; JupyterCell will handle both
                        return { ...cell, source: Array.isArray(cell.source) ? [cleaned] : cleaned };
                    }
                    return cell;
                }),
            };
        } catch (e) {
            console.error('Failed to sanitize notebook frontmatter', e);
            sanitizedNotebook = notebook;
        }

        return (
            <div className={small ? 'prose prose-sm max-w-none' : 'prose max-w-none'}>
                <div className="jupyter-notebook">
                    {sanitizedNotebook.cells.map((cell, idx) => (
                        <JupyterCell key={idx} cell={cell} index={idx} setToast={setToast} theme={effectiveTheme} />
                    ))}
                </div>
            </div>
        );
    }

    // Regular markdown rendering
    // Replace <p> tags with <div> to prevent nesting issues when rendered inside other elements
    const processedContent = content
        .replace(/^---[\s\S]*?---/, '')
        .replace(/<p(\s[^>]*)?>/gi, '<div$1>')
        .replace(/<\/p>/gi, '</div>')
        .trim();
    const components = MarkdownComponents(setToast, effectiveTheme);

    return (
        <div className={small ? 'prose prose-sm max-w-none' : 'prose max-w-none'}>
            <ReactMarkdown
                components={components}
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex, rehypeRaw]}
            >
                {processedContent}
            </ReactMarkdown>
        </div>
    );
};

export default MarkdownRenderer;
